import React, { useContext, useEffect, useState } from 'react'
import { Redirect, Route, Switch, useHistory } from 'react-router-dom'
import routes from '../routes'
import { UserPreferencesContext } from '../contexts/preferences/UserPreferencesContext'
import ProtectedRoute from '../components/ProtectedRoute'
import { LoadingCircular } from '../components/LoadingCircular'
import { canFromPermissions } from '../userprofile/canFromPermissions'
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk'
import { getAndClearReturnPath } from '../contexts/auth/commons'

/* -------------------------------------------------------------------------------------------------
 * Lazy Imports
 * -----------------------------------------------------------------------------------------------*/

const Programs = React.lazy(() => import('./Programs'))
const Analyses = React.lazy(() => import('./Analyses'))
const Combos = React.lazy(() => import('./Combos'))
const ClientHierarchy = React.lazy(() => import('./ClientHierarchy'))
const Users = React.lazy(() => import('./Users'))
const Backoffice = React.lazy(() => import('./BackOffice'))
const Bulks = React.lazy(() => import('./Bulks'))

/* -------------------------------------------------------------------------------------------------
 * Redirect from root: after re-login (e.g. token expiry) send user back to the page they were on.
 * -----------------------------------------------------------------------------------------------*/
const RootRedirect: React.FC = () => {
  const history = useHistory()
  useEffect(() => {
    const returnPath = getAndClearReturnPath()
    history.replace(returnPath || routes.programs)
  }, [history])
  return <LoadingCircular />
}

const Routing: React.FC = () => {
  const { permissions } = useContext(UserPreferencesContext)
  const { fvcUpdatesMenu, bulkPdf, clientsMenu } = useFlags()
  const ldClient = useLDClient()
  const [isLDReady, setIsLDReady] = useState(false)

  useEffect(() => {
    if (ldClient) {
      ldClient
        .waitUntilReady()
        .then(() => setIsLDReady(true))
        .catch(() => setIsLDReady(true))
    }
  }, [ldClient])

  if (!ldClient || !isLDReady || !permissions) return <LoadingCircular />

  const can = canFromPermissions(permissions)
  const canSeeUsers = can('USERS|USER_MANAGEMENT|READ_ALL')

  return (
    <React.Suspense fallback={<LoadingCircular />}>
      <Switch>
        <Route path={routes.analyses} component={Analyses} />
        <Route path={routes.combos} component={Combos} />
        <Route path={routes.programs} component={Programs} />
        <ProtectedRoute condition={clientsMenu} path={routes.clientHierarchy} component={ClientHierarchy} />
        <ProtectedRoute condition={bulkPdf} path={routes.bulks} component={Bulks} />
        <ProtectedRoute condition={canSeeUsers} path={routes.users} component={Users} />
        <ProtectedRoute condition={fvcUpdatesMenu} path={routes.backOffice} component={Backoffice} />
        <Route exact path="/" component={RootRedirect} />
        <Redirect to={routes.programs} />
      </Switch>
    </React.Suspense>
  )
}

Routing.displayName = 'AppRouting_Internal'

export default Routing
